/**
 * Sidebar v2 snooze presets and labels.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.snooze.ts`
 * at pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: preset resolution comes from this
 * port's sidebarV2Settled module (forge session types) and forge's
 * timestamp formatting; forge has no week-start setting, so the custom
 * snooze calendar keeps its library default.
 */
import { useEffect } from "react";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";

import { formatShortTimestamp } from "~/timestampFormat";

export type { SnoozePreset } from "./sidebarV2Settled";
import {
  resolveSharedSnoozePresets as resolveLocalPresets,
  type SnoozePreset,
} from "./sidebarV2Settled";

/** Format a snooze wake time using the selected timestamp preference. */
function timeOfDayLabel(date: Date, timestampFormat: TimestampFormat): string {
  return formatShortTimestamp(date.toISOString(), timestampFormat);
}

/** Build local snooze presets and format wake labels, including the weekday for next-week choices. */
export function resolveSnoozePresets(
  now: Date,
  timestampFormat: TimestampFormat,
): ReadonlyArray<SnoozePreset> {
  return resolveLocalPresets(now).map((preset) => {
    const wakeMs = Date.parse(preset.snoozedUntil);
    if (Number.isNaN(wakeMs)) return preset;
    const wake = new Date(wakeMs);
    const time = timeOfDayLabel(wake, timestampFormat);
    const whenLabel =
      preset.id === "next-week"
        ? `${wake.toLocaleDateString(undefined, { weekday: "short" })} ${time}`
        : time;
    return Object.assign({}, preset, { whenLabel });
  });
}

/** Arm a timeout for the earliest upcoming snooze wake so the shelf empties
 * the moment a snooze expires instead of on the next minute tick. The tick
 * is part of the schedule: setTimeout delays are signed 32-bit, so a wake
 * further out than ~24.8 days fires at the clamp and bumps the tick, and
 * the tick in the effect dependencies re-arms the timer until the wake is
 * in range. */
export function useSnoozeWakeTimer(
  firstWakeAt: string | null,
  snoozeWakeTick: number,
  bumpSnoozeWakeTick: (tick: (tick: number) => number) => void,
): void {
  useEffect(() => {
    const nextWakeMs = firstWakeAt == null ? Number.NaN : Date.parse(firstWakeAt);
    if (Number.isNaN(nextWakeMs)) return;
    const delayMs = Math.min(Math.max(0, nextWakeMs - Date.now()) + 50, 2_147_483_647);
    const id = window.setTimeout(() => bumpSnoozeWakeTick((tick) => tick + 1), delayMs);
    return () => window.clearTimeout(id);
  }, [bumpSnoozeWakeTick, firstWakeAt, snoozeWakeTick]);
}
